import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FollowUpAction, StepInfo, RecipePreviewMessage } from '../types/protocol';
import { MessageList } from './MessageList';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { StepTracker } from './StepTracker';
import { ActiveField } from './ActiveField';
import { RecipePreview } from './RecipePreview';
import { QuickActions } from './QuickActions';
import { PreviewRenderer } from './PreviewRenderer';
import type { UseChatReturn } from '../hooks/useChat';
import type { UseWebSocketReturn } from '../hooks/useWebSocket';

interface ChatPanelProps {
  chat: UseChatReturn;
  ws: UseWebSocketReturn;
  mode?: 'fullpage' | 'drawer' | 'inline';
  locale?: string;
  devMode?: boolean;
}

const COMMAND_PHRASES = new Set([
  'cancel', 'stop', 'quit', 'exit', 'abort',
  'skip', 'skip this', 'next', 'skip it',
  'go back', 'previous', 'back', 'prev',
]);

export function ChatPanel({ chat, ws, mode = 'fullpage', locale, devMode }: ChatPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const chatInputRef = useRef<ChatInputHandle>(null);
  const isDrawer = mode === 'drawer';

  const handleSend = (text: string) => {
    if (!COMMAND_PHRASES.has(text.toLowerCase())) {
      chat.addUserMessage(text);
    }
    chat.clearIntentHint();
    ws.send({ type: 'message', text });
  };

  const handleTyping = (text: string) => {
    ws.send({ type: 'typing_preview', text });
  };

  const handleAnswerSubmit = (value: unknown) => {
    const question = chat.currentQuestion;
    if (question) {
      chat.trackAnswer(question.field, value);
    }
    ws.send({ type: 'answer', value, stepId: question?.stepId });
    chat.clearCurrentQuestion();
  };

  const handleConfirm = () => {
    chat.addUserMessage(t('chat.confirmed'));
    ws.send({ type: 'confirm' });
  };

  const handleStepClick = (stepId: string) => {
    ws.send({ type: 'goto_step', stepId });
  };

  const handleSearch = useCallback((stepId: string, query: string) => {
    ws.send({ type: 'search', stepId, query });
  }, [ws]);

  const handleHintClick = () => {
    if (chat.intentHint) {
      chat.clearIntentHint();
      ws.send({ type: 'activate_workflow', workflowId: chat.intentHint.workflowId });
    }
  };

  const handleDismissHint = () => {
    chat.clearIntentHint();
  };

  const handleActivateWorkflow = (workflowId: string) => {
    ws.send({ type: 'activate_workflow', workflowId });
  };

  const handleFollowUpAction = (action: FollowUpAction) => {
    if (action.type !== 'activate_workflow') {
      return;
    }

    chat.addUserMessage(action.label);
    chat.clearIntentHint();
    ws.send({ type: 'activate_workflow', workflowId: action.workflowId, prefill: action.prefill });
  };

  const handleFocusInput = useCallback(() => {
    chatInputRef.current?.focus();
  }, []);

  const hasWorkflow = chat.steps.length > 0;
  const inputDisabled = chat.connectionStatus !== 'context_set';

  // Show quick actions when chat is fresh (only event messages like "Connected to...")
  const hasUserContent = chat.messages.some(
    (m) => m.role === 'user' || (m.role === 'assistant' && m.subtype !== 'event'),
  );
  const showQuickActions = !hasUserContent && !hasWorkflow && chat.availableWorkflows.length > 0;

  // Find active recipe preview from the latest message
  const lastMsg = chat.messages[chat.messages.length - 1];
  const activeRecipe: RecipePreviewMessage | undefined =
    lastMsg?.recipePreview && !chat.currentQuestion ? lastMsg.recipePreview : undefined;

  // Preview: only exists when there's actual data to show
  const hasPreview = chat.previewConfig != null
    && chat.previewData != null
    && Array.isArray((chat.previewData as Record<string, unknown>)?.nodes)
    && ((chat.previewData as Record<string, unknown>).nodes as unknown[]).length > 0;
  const previewSide = chat.previewConfig?.position === 'side';

  // Drawer mode: two-column (sidebar + main) when workflow active
  if (isDrawer) {
    const chatContent = (
      <>
        {hasWorkflow && (
          <aside className="sp-drawer-sidebar">
            <StepTracker
              steps={chat.steps as StepInfo[]}
              workflowName={chat.workflowName}
              workflowDescription={chat.workflowDescription}
              onStepClick={handleStepClick}
            />
          </aside>
        )}
        <main className="sp-main">
          {showQuickActions ? (
            <div className="sp-message-list">
              <QuickActions
                workflows={chat.availableWorkflows}
                onActivate={handleActivateWorkflow}
                onFocusInput={handleFocusInput}
              />
            </div>
          ) : (
            <MessageList
              messages={chat.messages}
              isThinking={chat.isThinking}
              onConfirm={handleConfirm}
              onEdit={handleStepClick}
              onAction={handleFollowUpAction}
              devMode={devMode}
            />
          )}
          {hasPreview && !previewSide && (
            <div className="sp-preview-below">
              <PreviewRenderer
                previewType={chat.previewConfig!.type}
                data={chat.previewData}
                label={chat.previewConfig!.label}
              />
            </div>
          )}
          {activeRecipe ? (
            <div className="sp-active-field">
              <RecipePreview
                preview={activeRecipe}
                onConfirm={handleConfirm}
                onEdit={handleStepClick}
              />
            </div>
          ) : (
            <ActiveField
              key={chat.currentQuestion?.stepId}
              question={chat.currentQuestion}
              onSubmit={handleAnswerSubmit}
              steps={hasWorkflow ? (chat.steps as StepInfo[]) : undefined}
              onNavigate={hasWorkflow ? handleStepClick : undefined}
              previewData={chat.previewData}
            />
          )}
          <ChatInput
            ref={chatInputRef}
            onSend={handleSend}
            onTyping={handleTyping}
            disabled={inputDisabled}
            intentHint={chat.intentHint}
            onHintClick={handleHintClick}
            onDismissHint={handleDismissHint}
            locale={locale}
            inWorkflow={hasWorkflow}
            placeholder={
              inputDisabled
                ? t('input.placeholderCluster')
                : chat.currentQuestion
                  ? t('input.placeholderAnswer')
                  : t('input.placeholderDescribe')
            }
          />
        </main>
      </>
    );

    return (
      <div className={`sp-chat-panel sp-chat-panel-drawer${hasWorkflow ? ' sp-drawer-multicol' : ''}${hasPreview && previewSide ? ' sp-has-preview-side' : ''}`}>
        {hasPreview && previewSide && (
          <aside className="sp-preview-side">
            <PreviewRenderer
              previewType={chat.previewConfig!.type}
              data={chat.previewData}
              label={chat.previewConfig!.label}
            />
          </aside>
        )}
        <div className={`sp-chat-area${hasWorkflow ? ' sp-drawer-multicol' : ''}`}>
          {chatContent}
        </div>
      </div>
    );
  }

  // Fullpage / inline: standard sidebar layout
  return (
    <div className={`sp-chat-panel${hasWorkflow ? ' sp-has-workflow' : ''}${hasPreview && previewSide ? ' sp-has-preview-side' : ''}`}>
      {hasWorkflow && (
        <aside className="sp-sidebar">
          <StepTracker
            steps={chat.steps as StepInfo[]}
            workflowName={chat.workflowName}
            workflowDescription={chat.workflowDescription}
            onStepClick={handleStepClick}
          />
        </aside>
      )}
      <main className="sp-main">
        {showQuickActions ? (
          <div className="sp-message-list">
            <QuickActions
              workflows={chat.availableWorkflows}
              onActivate={handleActivateWorkflow}
              onFocusInput={handleFocusInput}
            />
          </div>
        ) : (
          <MessageList
            messages={chat.messages}
            isThinking={chat.isThinking}
            onConfirm={handleConfirm}
            onEdit={handleStepClick}
            onAction={handleFollowUpAction}
            devMode={devMode}
          />
        )}
        {hasPreview && !previewSide && (
          <div className="sp-preview-below">
            <PreviewRenderer
              previewType={chat.previewConfig!.type}
              data={chat.previewData}
              label={chat.previewConfig!.label}
            />
          </div>
        )}
        {activeRecipe ? (
          <div className="sp-active-field">
            <RecipePreview
              preview={activeRecipe}
              onConfirm={handleConfirm}
              onEdit={handleStepClick}
            />
          </div>
        ) : (
          <ActiveField
            key={chat.currentQuestion?.stepId}
            question={chat.currentQuestion}
            onSubmit={handleAnswerSubmit}
            onSearch={handleSearch}
            steps={hasWorkflow ? (chat.steps as StepInfo[]) : undefined}
            onNavigate={hasWorkflow ? handleStepClick : undefined}
            previewData={chat.previewData}
          />
        )}
        <ChatInput
          ref={chatInputRef}
          onSend={handleSend}
          onTyping={handleTyping}
          disabled={inputDisabled}
          intentHint={chat.intentHint}
          onHintClick={handleHintClick}
          onDismissHint={handleDismissHint}
          inWorkflow={hasWorkflow}
          placeholder={
            inputDisabled
              ? t('input.placeholderCluster')
              : chat.currentQuestion
                ? t('input.placeholderAnswer')
                : t('input.placeholderDescribe')
          }
        />
      </main>
      {hasPreview && previewSide && (
        <aside className="sp-preview-side">
          <PreviewRenderer
            previewType={chat.previewConfig!.type}
            data={chat.previewData}
            label={chat.previewConfig!.label}
          />
        </aside>
      )}
    </div>
  );
}
